<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from "vue";
import Footer from "~/components/Footer.vue";
import Header from "~/components/Header.vue";
import MobileBottomNav from "~/components/mobile/MobileBottomNav.vue";
import MobileHeader from "~/components/mobile/MobileHeader.vue";
import MobileMenuSheet from "~/components/mobile/MobileMenuSheet.vue";
import NavBar from "~/components/NavBar.vue";
import { onMounted, useNuxtApp, useRoute } from "#imports";
import { useCartStore } from "~/stores/cartStore";
import { useLoginResponse } from "~/composables/useLoginResponse";

const nuxtApp = useNuxtApp();
const cartStore = useCartStore();
const route = useRoute();
const { hydrateLoginResponse, canViewCart } = useLoginResponse();
const mobileMenuOpen = ref(false);
const showDesktopChat = ref(false);
let desktopMediaQuery: MediaQueryList | null = null;
let handleDesktopMediaChange: (() => void) | null = null;
const $useOneCApi = nuxtApp.$useOneCApi as (
  action: string,
  payload?: Record<string, any>,
) => Promise<any>;

const fetchCartCount = async () => {
  if (process.server) return;
  hydrateLoginResponse();
  if (!canViewCart.value) {
    cartStore.setCount(0);
    return;
  }
  // Избегаем двойного запроса с страницей корзины
  if (route.path === "/cart") return;
  try {
    const res = await $useOneCApi("getCart", {});
    const items = Array.isArray(res?.Товары) ? res.Товары : [];
    cartStore.setCount(items.length);
  } catch (error) {
    console.error("Failed to load cart count", error);
  }
};

onMounted(() => {
  fetchCartCount();

  if (!window.matchMedia) {
    showDesktopChat.value = true;
    return;
  }

  desktopMediaQuery = window.matchMedia("(min-width: 1176px)");
  handleDesktopMediaChange = () => {
    showDesktopChat.value = desktopMediaQuery?.matches ?? true;
  };

  handleDesktopMediaChange();
  desktopMediaQuery.addEventListener?.("change", handleDesktopMediaChange);
});

onBeforeUnmount(() => {
  if (!desktopMediaQuery || !handleDesktopMediaChange) return;
  desktopMediaQuery.removeEventListener?.("change", handleDesktopMediaChange);
});

watch(
  () => route.fullPath,
  () => {
    mobileMenuOpen.value = false;
  },
);
</script>

<template>
  <div
    class="app-layout min-h-screen overflow-x-hidden bg-surface-base lg:bg-transparent"
  >
    <MobileHeader />
    <MobileMenuSheet v-model="mobileMenuOpen" />
    <MobileBottomNav @open-menu="mobileMenuOpen = true" />
    <NavBar
      class="fixed left-0 top-0 z-[1000] hidden h-screen w-[var(--layout-sidebar-width)] lg:flex"
    />
    <Header
      class="site-header fixed left-[var(--layout-sidebar-width)] right-0 top-0 z-[1002] hidden lg:flex"
    />
    <div
      class="flex min-h-screen min-w-0 flex-col pt-[var(--layout-mobile-header-height)] lg:pl-[var(--layout-sidebar-width)] lg:pt-[var(--layout-header-height)]"
    >
      <Chat v-if="showDesktopChat && !route.path.startsWith('/chat')" />
      <FirstOrderDiscount />
      <main class="flex-1 min-w-0">
        <slot />
      </main>
      <Footer v-if="!route.path.startsWith('/chat')" />
    </div>
  </div>
</template>

<style scoped>
/* Keep header popovers above the offer without casting a shadow over it. */
.app-layout:has(.first-order-discount) .site-header {
  box-shadow: none;
}
</style>
